/**
 * Lectura de la ubicación del dispositivo.
 *
 * ADR-006 hace del GPS un requisito, pero `docs/03` §5 pone la condición:
 * se pide **con el beneficio en pantalla**, y negarlo no puede ser un
 * callejón — siempre queda mover el mapa y poner el pin a mano.
 *
 * Devuelve un `Result` en vez de lanzar porque los tres desenlaces exigen
 * pantallas distintas, y la API del navegador los mezcla en un solo
 * callback de error con un código numérico.
 */

export interface Position {
  lat: number;
  lng: number;
  /** Radio de incertidumbre en metros, tal como lo reporta el navegador. */
  accuracyM: number;
}

export type GeoError = 'permission_denied' | 'unavailable' | 'timeout';

export type GeoResult =
  | { ok: true; value: Position }
  | { ok: false; error: GeoError };

/**
 * Más de esto y la persona ya se fue.
 *
 * Dentro de un local el GPS puede no responder nunca: sin límite, la
 * pantalla se queda en "buscando…" para siempre. 12 s es largo para una
 * espera pero corto frente a un cuelgue infinito, y al vencer se ofrece
 * poner el pin a mano.
 */
export const GEO_TIMEOUT_MS = 12_000;

export async function readPosition(): Promise<GeoResult> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, error: 'unavailable' };
  }

  return new Promise<GeoResult>((resolve) => {
    let resuelto = false;
    const acabar = (r: GeoResult) => {
      if (resuelto) return;
      resuelto = true;
      resolve(r);
    };

    /**
     * Temporizador propio además del `timeout` de la API.
     *
     * El nativo no siempre dispara: en algunos navegadores, con el permiso
     * concedido y el sensor mudo, el callback no llega nunca y tampoco el
     * error. Este garantiza que la pantalla avance.
     */
    const t = setTimeout(() => acabar({ ok: false, error: 'timeout' }), GEO_TIMEOUT_MS);

    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(t);
        acabar({
          ok: true,
          value: {
            lat: p.coords.latitude,
            lng: p.coords.longitude,
            accuracyM: p.coords.accuracy,
          },
        });
      },
      (e) => {
        clearTimeout(t);
        // 1 = PERMISSION_DENIED · 2 = POSITION_UNAVAILABLE · 3 = TIMEOUT
        const error: GeoError =
          e.code === 1 ? 'permission_denied' : e.code === 3 ? 'timeout' : 'unavailable';
        acabar({ ok: false, error });
      },
      {
        // Sin esto el navegador puede resolver por IP o torre de celda y
        // devolver un punto a kilómetros: suficiente para un mapa, inútil
        // para decir "estuviste aquí" con un geofence de 120 m.
        enableHighAccuracy: true,
        timeout: GEO_TIMEOUT_MS,
        // Una lectura de hace un minuto sirve: la persona no se movió, y
        // ahorra encender el GPS otra vez.
        maximumAge: 60_000,
      },
    );
  });
}
